import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import OtpInput from './components/OtpInput';
import OtpVerification from './components/OtpVerification';
import Icon from '../../components/AppIcon';
import { OtpType } from '../../services/otpService';

type OtpStep = 'input' | 'verify';

const OtpLogin: React.FC = () => {
  const navigate = useNavigate();
  const { sendOtp } = useAuth();
  
  const [step, setStep] = useState<OtpStep>('input');
  const [identifier, setIdentifier] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!identifier) {
      setError('Please enter your email');
      return;
    }

    // Basic validation
    if (!/\S+@\S+\.\S+/.test(identifier)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await sendOtp(identifier, 'email');

      if (!result.success) {
        setError(result.error || 'Failed to send OTP');
        return;
      }

      setSuccessMessage(result.message || 'OTP sent successfully!');
      setTimeout(() => {
        setStep('verify');
      }, 500);
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSuccess = (user: any) => {
    // Navigate to appropriate dashboard based on user profile
    navigate('/');
  };

  const handleCancel = () => {
    setStep('input');
    setError('');
    setSuccessMessage('');
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-primary rounded-full mix-blend-screen filter blur-3xl animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-primary-light rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-primary rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-md relative z-10">
        <div className="rounded-2xl shadow-2xl p-8 border border-dark-700 bg-dark-800 backdrop-blur-xl">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img
                src="/assets/images/Noonx_logo_small.png"
                alt="NOONX Platform Logo"
                className="h-16 w-auto object-contain"
                loading="eager"
                fetchpriority="high"
              />
            </div>
            {step === 'input' && (
              <>
                <h1 className="text-3xl font-bold mb-2 text-white">Sign in with OTP</h1>
                <p className="text-gray-400">Get a one-time password sent to you</p>
              </>
            )}
          </div>

          {step === 'input' ? (
            <form onSubmit={handleSendOtp} className="space-y-6">
              {/* Input Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon 
                      name="Mail" 
                      size={18} 
                      className="text-gray-500" 
                    />
                  </div>
                  <input
                    type="email"
                    value={identifier}
                    onChange={(e) => {
                      setIdentifier(e.target.value);
                      setError('');
                    }}
                    placeholder="you@example.com"
                    className={`
                      w-full pl-10 pr-4 py-3 rounded-lg
                      bg-dark-700 border text-white
                      placeholder-gray-500
                      focus:outline-none focus:ring-2 focus:ring-primary/50
                      ${error ? 'border-red-500' : 'border-dark-600 focus:border-primary'}
                    `}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 text-sm">{successMessage}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !identifier}
                className={`
                  w-full py-3 px-4 rounded-lg font-medium text-white
                  transition-all duration-200
                  ${isLoading || !identifier
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-dark shadow-lg shadow-primary/30 hover:shadow-primary/50'
                  }
                `}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <Icon name="Loader2" size={20} className="animate-spin mr-2" />
                    Sending OTP...
                  </span>
                ) : (
                  <>
                    <Icon name="Send" size={18} className="inline mr-2" />
                    Send OTP
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-dark-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-dark-800 text-gray-500">or</span>
                </div>
              </div>

              {/* Back to Password Login */}
              <button
                type="button"
                onClick={() => navigate('/authentication')}
                className="w-full py-3 px-4 rounded-lg font-medium text-gray-300 border border-dark-600 hover:border-primary hover:text-primary transition-all"
              >
                <Icon name="Lock" size={18} className="inline mr-2" />
                Sign in with Password
              </button>
            </form>
          ) : (
            <OtpVerification
              identifier={identifier}
              otpType="email"
              onSuccess={handleVerificationSuccess}
              onCancel={handleCancel}
            />
          )}

          {/* Footer Links */}
          <div className="mt-8 pt-6 border-t border-dark-600">
            <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
              <button
                type="button"
                onClick={() => navigate('/landing-page')}
                className="hover:text-primary transition-all font-medium"
              >
                Back to Home
              </button>
              <span>•</span>
              <button
                type="button"
                onClick={() => navigate('/authentication', { state: { mode: 'register' } })}
                className="hover:text-primary transition-all font-medium"
              >
                Create Account
              </button>
            </div>
          </div>
        </div>

        {/* Terms */}
        <div className="mt-4 text-center text-xs text-gray-400">
          <p>By continuing, you agree to NOONX's</p>
          <p className="mt-1">
            <button type="button" onClick={() => navigate('/terms-of-service')} className="hover:text-white transition-all">
              Terms of Service
            </button>
            {' and '}
            <button type="button" onClick={() => navigate('/privacy-policy')} className="hover:text-white transition-all">
              Privacy Policy
            </button>
          </p>
        </div>
      </div>

      {/* Invisible reCAPTCHA container for Firebase phone auth */}
      <div id="recaptcha-container"></div>
    </div>
  );
};

export default OtpLogin;
